import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useIdentity } from "../../auth/useIdentity";
import {
  decideConnectionRequest,
  listConnectionRequests,
  type ConnectionRequest,
  type ConnectionRequestDecision,
} from "../../api/connectionRequests";

const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));
const when = (s?: string) => (s ? new Date(s).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", dateStyle: "medium", timeStyle: "short" }) : "");

/**
 * New connection requests (owner 2026-11-02): customers who asked on the public
 * sign-up page for a residential or commercial connection. Nothing has been paid
 * and no plan is given here: contact them, install, then Installed with the
 * ONU's MAC. The customer then pays for the plan asked for (owner 2026-11-03).
 */
export function ConnectionRequestsScreen() {
  const identity = useIdentity();
  const ns = identity.namespace ?? "";
  const qc = useQueryClient();
  const [closed, setClosed] = useState(false);
  const [declining, setDeclining] = useState<ConnectionRequest | null>(null);
  const [installing, setInstalling] = useState<ConnectionRequest | null>(null);

  const requests = useQuery({
    queryKey: ["connectionrequests", ns, closed],
    enabled: Boolean(identity.token && ns),
    queryFn: ({ signal }) => listConnectionRequests(identity, ns, closed, signal),
    retry: false,
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: ["connectionrequests", ns] });
  const decide = useMutation({
    mutationFn: ({ r, status }: { r: ConnectionRequest; status: ConnectionRequestDecision }) => decideConnectionRequest(identity, ns, r.id, status, ""),
    onSettled: refresh,
  });

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography variant="h5" component="h1">New connection requests</Typography>
        <Typography variant="body2" color="text.secondary">
          Customers who asked for a connection on your sign-up page. Nothing has been paid. After installing, press Installed and enter the ONU MAC: the customer is then asked to pay, and the internet starts once paid.
        </Typography>
      </Box>
      <Tabs value={closed ? "closed" : "open"} onChange={(_, v: string) => setClosed(v === "closed")}>
        <Tab value="open" label="Open" />
        <Tab value="closed" label="Closed" />
      </Tabs>
      {requests.error && <Alert severity="error">{errText(requests.error)}</Alert>}
      {decide.error && <Alert severity="error">{errText(decide.error)}</Alert>}
      {requests.data && requests.data.length === 0 && <Alert severity="info">{closed ? "None." : "No open request."}</Alert>}
      {requests.data && requests.data.length > 0 && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Asked</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Plan</TableCell>
                <TableCell>Address</TableCell>
                <TableCell>{closed ? "Decided" : "Status"}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.data.map((r) => (
                <TableRow key={r.id}>
                  <TableCell>{when(r.created)}</TableCell>
                  <TableCell>
                    {r.name}
                    <Typography variant="caption" display="block" color="text.secondary">{r.mobile}</Typography>
                  </TableCell>
                  <TableCell>{r.plan_heading || r.groupname}</TableCell>
                  <TableCell>{r.address}, {r.city}, {r.state} {r.pin}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    <Chip size="small" color={r.status === "installed" ? "success" : "default"} label={r.status} />
                    {closed ? (
                      <>
                        {r.decided_by && <Typography variant="caption" display="block" color="text.secondary">{r.decided_by}</Typography>}
                        {r.reason && <Typography variant="caption" display="block">{r.reason}</Typography>}
                      </>
                    ) : (
                      <Box sx={{ mt: 0.5 }}>
                        {r.status === "new" && <Button size="small" disabled={decide.isPending} onClick={() => decide.mutate({ r, status: "contacted" })}>Contacted</Button>}
                        <Button size="small" variant="contained" disabled={decide.isPending} onClick={() => setInstalling(r)}>Installed</Button>{" "}
                        <Button size="small" color="error" onClick={() => setDeclining(r)}>Decline</Button>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {installing && <InstallDialog ns={ns} request={installing} onClose={() => setInstalling(null)} onDone={() => { refresh(); setInstalling(null); }} />}
      {declining && <DeclineDialog ns={ns} request={declining} onClose={() => setDeclining(null)} onDone={() => { refresh(); setDeclining(null); }} />}
    </Box>
  );
}

function DeclineDialog({ ns, request, onClose, onDone }: { ns: string; request: ConnectionRequest; onClose: () => void; onDone: () => void }) {
  const identity = useIdentity();
  const [reason, setReason] = useState("");
  const decline = useMutation({ mutationFn: () => decideConnectionRequest(identity, ns, request.id, "declined", reason), onSuccess: onDone });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Decline the request from {request.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="We do not serve this area yet"
            inputProps={{ maxLength: 200 }}
            fullWidth
          />
          {decline.error && <Alert severity="error">{errText(decline.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" color="error" disabled={reason.trim() === "" || decline.isPending} onClick={() => decline.mutate()}>Decline</Button>
      </DialogActions>
    </Dialog>
  );
}

const MAC = /^[0-9a-f]{2}([:\-.]?[0-9a-f]{2}){5}$/i;

function InstallDialog({ ns, request, onClose, onDone }: { ns: string; request: ConnectionRequest; onClose: () => void; onDone: () => void }) {
  const identity = useIdentity();
  const [onu, setOnu] = useState("");
  const ok = MAC.test(onu.trim());
  const install = useMutation({ mutationFn: () => decideConnectionRequest(identity, ns, request.id, "installed", "", onu), onSuccess: onDone });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Installed for {request.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2">
            The ONU becomes {request.mobile}&apos;s login. It signs in once the customer pays for {request.plan_heading || request.groupname}.
          </Typography>
          <TextField
            label="ONU MAC"
            value={onu}
            onChange={(e) => setOnu(e.target.value)}
            placeholder="AA:BB:CC:DD:EE:FF"
            error={onu.trim() !== "" && !ok}
            helperText={onu.trim() !== "" && !ok ? "Not a MAC address" : " "}
            inputProps={{ maxLength: 17 }}
            fullWidth
            autoFocus
          />
          {install.error && <Alert severity="error">{errText(install.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!ok || install.isPending} onClick={() => install.mutate()}>{install.isPending ? "Saving…" : "Installed"}</Button>
      </DialogActions>
    </Dialog>
  );
}
